import {
  Badge,
  formatPrice,
  HStack,
  StackDivider,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
} from 'design-system';
import { useMemo } from 'react';

/** `formatPrice` blanks 0; an edited line can legitimately go to or come from 0. */
const formatUnitPrice = (value: number) => formatPrice(value) || Number(value ?? 0).toFixed(2);

interface IOrderItemsTable {
  totalItems: number;
  subtotal: number;
  vat: number;
  vatPercent: number;
  total: number;
  currencyCode: string;
  orderStatus: string;
  items: {
    name: string;
    price: number;
    quantity: number;
    unit: string;
    receivedQuantity: number;
    confirmedQuantity: number;
    totalPrice: number;
    /** The restaurant changed the unit price on receive (backend design §5): `price` is the new one. */
    receivedPriceEdited?: boolean;
    /** The confirmed unit price, else the ordered one: the old price of an edited line. */
    expectedUnitPrice?: number | null;
  }[];
}

export const OrderItemsTable = ({
  items,
  totalItems,
  subtotal,
  vat,
  vatPercent,
  total,
  currencyCode,
  orderStatus,
}: IOrderItemsTable) => {
  const isReceived = useMemo(() => {
    return orderStatus === 'received';
  }, []);

  return (
    <VStack
      spacing='lg'
      flex={1}
      alignItems='stretch'
      divider={<StackDivider borderColor='gray.200' />}
      overflowY='auto'>
      <TableContainer flex={1} overflowY='auto'>
        <Table size='sm' variant='simple'>
          <Thead>
            <Tr>
              <Th>Items</Th>
              <Th>Unit</Th>
              <Th>QTY</Th>
              <Th>{isReceived ? 'Received QTY' : 'Confirmed QTY'}</Th>
              <Th>Ind.Price</Th>
              <Th>Tot.Price</Th>
            </Tr>
          </Thead>
          <Tbody>
            {items?.map((item, i) => {
              return (
                <Tr key={i}>
                  <Td>
                    <Text>{item?.name}</Text>
                  </Td>
                  <Td>{item?.unit}</Td>
                  <Td>{item?.quantity}</Td>
                  <Td>{isReceived ? item?.receivedQuantity : item?.confirmedQuantity}</Td>
                  <Td>
                    {item?.receivedPriceEdited ? (
                      <HStack spacing='xs'>
                        {item?.expectedUnitPrice != null && (
                          <>
                            <Text as='s' color='gray.500'>
                              {formatUnitPrice(item.expectedUnitPrice)}
                            </Text>
                            <Text color='gray.500'>→</Text>
                          </>
                        )}
                        <Text fontWeight='semibold'>{formatUnitPrice(item?.price)}</Text>
                        <Tooltip label='The restaurant changed this price on receive' hasArrow>
                          <Badge colorScheme='orange' variant='subtle' fontSize='2xs' textTransform='none'>
                            Edited
                          </Badge>
                        </Tooltip>
                      </HStack>
                    ) : (
                      formatPrice(item?.price)
                    )}
                  </Td>
                  <Td>{formatPrice(item?.totalPrice)}</Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </TableContainer>

      <VStack spacing='md' alignItems='stretch' fontWeight={'bold'}>
        <HStack justify='space-between'>
          <Text>Total (Items)</Text>
          <Text>{totalItems}</Text>
        </HStack>
        <HStack justify='space-between'>
          <Text>Subtotal</Text>
          <Text>{formatPrice(subtotal, currencyCode)}</Text>
        </HStack>
        <HStack justify='space-between'>
          <Text>VAT ({vatPercent}%)</Text>
          <Text>{formatPrice(vat, currencyCode)}</Text>
        </HStack>
        <HStack justify='space-between' color='red.500'>
          <Text>Total(Inc.VAT)</Text>
          <Text>{formatPrice(total, currencyCode)}</Text>
        </HStack>
      </VStack>
    </VStack>
  );
};
